import React, { useEffect, useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import moment from 'moment-timezone'
import { Input } from 'reactstrap'
import DatetimeRenderer from './DatetimeRenderer'
import './HistoryList.css'

const BQHistoryQuery = gql`
    query BQHistoryQuery($objectIds: [ID!]!, $startDate: Date, $endDate: Date) {
        bqHistory(objectIds: $objectIds, startDate: $startDate, endDate: $endDate) {
            items {
                appLabel
                modelName
                objectId
                eventTimestamp
                actorName
                isNew
                isDeleted
                snapshot
                dirtyFields
                comment
            }
        }
    }
`

const formatJson = (value) => {
    if (value === null || value === undefined) return '--'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const HIDDEN_CHANGE_FIELDS = new Set(['date_modified'])
const SNAPSHOT_FIELD_ALIASES = {
    modified_by: 'modified_by_id',
    created_by: 'created_by_id',
    assigned_by: 'assigned_by_id',
}

// Values are shown as written for text, and NULL where there was no value at
// all -- an empty cell would read as "we don't know" rather than "it was unset".
const formatChangeValue = (value) => {
    if (value === null || value === undefined || value === '') return 'NULL'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

// Humanize a field/model name: drop a trailing _id, split on _, Title Case.
const humanizeName = (name) => String(name || '')
    .replace(/_id$/, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const joinReadable = (labels) => {
    if (labels.length === 0) return ''
    if (labels.length === 1) return labels[0]
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

// When the backend has no comment for a row, summarize the event/changes so the
// timeline still shows a human-readable description of what happened.
const generateSummary = (item, changedFieldNames) => {
    const modelLabel = humanizeName(item.modelName) || 'record'
    if (item.isNew) return `New ${modelLabel.toLowerCase()} created.`
    if (item.isDeleted) return `${modelLabel} deleted.`
    if (!changedFieldNames.length) return 'Updated with no tracked field changes.'
    return `Updated ${joinReadable(changedFieldNames.map(humanizeName))}.`
}

const normalizeForComparison = (value) => {
    if (value === null || value === undefined || value === '') return null
    if (Array.isArray(value)) return value.map(normalizeForComparison)
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                acc[key] = normalizeForComparison(value[key])
                return acc
            }, {})
    }
    return value
}

const areValuesEqual = (left, right) => (
    JSON.stringify(normalizeForComparison(left)) === JSON.stringify(normalizeForComparison(right))
)

const getSnapshotFieldName = (fieldName) => SNAPSHOT_FIELD_ALIASES[fieldName] || fieldName
const MAX_HISTORY_RANGE_DAYS = 90
const ISO = moment.HTML5_FMT.DATE

// One list, so choosing a window is one click and every option is visible at
// once: the recent windows people usually want, then named months for the
// "what happened last March" case that used to mean typing two dates.
const RANGE_PRESETS = [7, 30, MAX_HISTORY_RANGE_DAYS]
const MONTHS_OFFERED = 24
const DEFAULT_RANGE_VALUE = 'last:7'

const lastDays = (days) => ({
    start: moment().subtract(days - 1, 'days').format(ISO),
    end: moment().format(ISO),
})

// A whole calendar month, clamped so the month still running does not ask for
// days that have not happened. Always well inside the window cap.
const monthRange = (month) => ({
    start: moment(month, 'YYYY-MM').startOf('month').format(ISO),
    end: moment.min(moment(month, 'YYYY-MM').endOf('month'), moment()).format(ISO),
})

const rangeFromValue = (value) => {
    const [kind, argument] = String(value).split(':')
    return kind === 'month' ? monthRange(argument) : lastDays(Number(argument))
}

const monthOptions = () => (
    Array.from({ length: MONTHS_OFFERED }, (_, index) => {
        const month = moment().subtract(index, 'months')
        return { value: `month:${month.format('YYYY-MM')}`, label: month.format('MMMM YYYY') }
    })
)

// A snapshot value can be a whole serialised object. Past this, one long value
// pushes the From/To pair off the row and buries every other change on it.
const VALUE_TRUNCATE_AT = 48

/**
 * A change value, shortened until it is asked for in full. Each one holds its
 * own state, so opening a long "To" leaves the "From" beside it untouched.
 */
function ChangeValue({ value }) {
    const [expanded, setExpanded] = useState(false)

    if (value.length <= VALUE_TRUNCATE_AT) {
        return <span className="vgm-audit-change-value">{value}</span>
    }

    return (
        <span className="vgm-audit-change-value">
            {expanded ? value : value.slice(0, VALUE_TRUNCATE_AT)}
            <button
                type="button"
                className="vgm-audit-value-toggle"
                aria-expanded={expanded}
                title={expanded ? 'Show less' : 'Show the full value'}
                onClick={() => setExpanded((current) => !current)}
            >
                {expanded ? 'less' : '…'}
            </button>
        </span>
    )
}

function BQHistoryList({ objectIds = [], emptyText = 'No history found yet.', subject = null }) {
    const [visibleDetails, setVisibleDetails] = useState({})
    // The chosen option *is* the range -- every option yields a valid one, so
    // there is nothing to validate, no draft, and no Apply.
    const [rangeValue, setRangeValue] = useState(DEFAULT_RANGE_VALUE)
    const range = useMemo(() => rangeFromValue(rangeValue), [rangeValue])
    const normalizedObjectIds = useMemo(
        () => (objectIds || []).filter(Boolean),
        [objectIds]
    )
    const isDefaultDateRange = rangeValue === DEFAULT_RANGE_VALUE

    useEffect(() => {
        setVisibleDetails({})
    }, [normalizedObjectIds.join('|'), range.start, range.end])

    const { data, loading, error } = useQuery(BQHistoryQuery, {
        variables: {
            objectIds: normalizedObjectIds,
            startDate: range.start,
            endDate: range.end,
        },
        skip: !normalizedObjectIds.length,
        fetchPolicy: 'cache-and-network',
    })

    const header = (
        <div className="vgm-audit-header">
            <div className="vgm-audit-header-titles">
                <h2 className="vgm-audit-title">Activity Logs</h2>
                <p className="vgm-audit-subtitle">
                    {subject ? `Every recorded change to ${subject}` : 'Every recorded change'}, who made it and when.
                </p>
            </div>
            <div className="vgm-audit-daterange">
                <label className="vgm-audit-range-label" htmlFor="bq-history-range">Showing</label>
                <Input
                    id="bq-history-range"
                    type="select"
                    className="vgm-audit-range-select"
                    value={rangeValue}
                    disabled={loading}
                    onChange={(e) => setRangeValue(e.target.value)}
                >
                    {RANGE_PRESETS.map((days) => (
                        <option key={days} value={`last:${days}`}>Last {days} days</option>
                    ))}
                    <optgroup label="By month">
                        {monthOptions().map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </optgroup>
                </Input>
            </div>
        </div>
    )

    const historyItems = data?.bqHistory?.items || []
    const getItemKey = (item, index) => (
        `${item.appLabel}-${item.modelName}-${item.objectId}-${item.eventTimestamp || index}-${index}`
    )
    const toggleDetails = (key) => {
        setVisibleDetails((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    if (loading && !historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <Skeleton height={90} count={4} className="mb-2" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <div className="alert alert-danger mb-0">{error.message}</div>
            </div>
        )
    }

    if (!historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <p className="vgm-audit-empty">
                    {isDefaultDateRange
                        ? 'No history found in the last 7 days. Try expanding the date range.'
                        : emptyText}
                </p>
            </div>
        )
    }

    // Callers pass a single object today, so the modal title already names the
    // subject and repeating it on every row is noise. Kept for the case where a
    // caller asks for several objects at once, where the row is ambiguous without it.
    const spansSeveralObjects = new Set(historyItems.map((item) => `${item.modelName}#${item.objectId}`)).size > 1
    const columnCount = spansSeveralObjects ? 6 : 5

    return (
        <div className="vgm-audit-trail">
            {header}
            <div className="vgm-audit-tablewrap">
                <table className="vgm-audit-table">
                    <thead>
                        <tr>
                            {/* Only earns a column when the log actually covers several
                                records -- otherwise the modal title already names the one.
                                Leads the row when it is there: it says which record the
                                rest of the row is about. */}
                            {spansSeveralObjects && <th className="vgm-audit-th">Object</th>}
                            <th className="vgm-audit-th">Activity</th>
                            <th className="vgm-audit-th">Who</th>
                            <th className="vgm-audit-th">When</th>
                            <th className="vgm-audit-th">Change</th>
                            <th className="vgm-audit-th vgm-audit-snapshot-col">
                                <span className="vgm-audit-sr-only">Snapshot</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {historyItems.map((item, index) => {
                                const itemKey = getItemKey(item, index)
                                const isEvent = item.isNew || item.isDeleted
                                const visibleChanges = !isEvent && item.dirtyFields && typeof item.dirtyFields === 'object'
                                    ? Object.entries(item.dirtyFields).filter(([fieldName, oldValue]) => {
                                        const snapshotFieldName = getSnapshotFieldName(fieldName)
                                        return (
                                            !HIDDEN_CHANGE_FIELDS.has(fieldName)
                                            && !areValuesEqual(oldValue, item.snapshot?.[snapshotFieldName])
                                        )
                                    })
                                    : []
                                const isDetailsVisible = visibleDetails[itemKey]
                                // Use the backend comment when present; otherwise generate a
                                // summary of the event/changes so every row has a description.
                                const description = item.comment
                                    || generateSummary(item, visibleChanges.map(([fieldName]) => fieldName))
                                // The dot's colour is the only ornament here, and it carries
                                // information: created, deleted, or a plain update.
                                const kind = item.isNew ? 'created' : (item.isDeleted ? 'deleted' : 'updated')
                                return (
                                    <React.Fragment key={itemKey}>
                                        <tr className={`vgm-audit-row${isDetailsVisible ? ' is-open' : ''}`}>
                                            {spansSeveralObjects && (
                                                <td className="vgm-audit-cell vgm-audit-related-cell">
                                                    <span className="vgm-audit-object">
                                                        {humanizeName(item.modelName) || 'Record'}
                                                    </span>
                                                    <span className="vgm-audit-object-id">#{item.objectId}</span>
                                                </td>
                                            )}
                                            <td className="vgm-audit-cell vgm-audit-activity-cell">
                                                <div className="vgm-audit-activity">
                                                    <span className={`vgm-audit-marker is-${kind}`} aria-hidden="true" />
                                                    <span>{description}</span>
                                                </div>
                                            </td>
                                            <td className="vgm-audit-cell">
                                                <span className={`vgm-audit-actor${item.actorName ? '' : ' is-system'}`}>
                                                    {item.actorName || 'System'}
                                                </span>
                                            </td>
                                            <td className="vgm-audit-cell vgm-audit-when-cell">
                                                {/* "4 days ago" answers the question a bare stamp
                                                    only lets you work out; the exact moment, in every
                                                    timezone that matters, is a hover away. */}
                                                <span className="vgm-audit-when">
                                                    {item.eventTimestamp
                                                        ? <DatetimeRenderer datetime={item.eventTimestamp} label={moment(item.eventTimestamp).fromNow()} placement="top" />
                                                        : '--'}
                                                </span>
                                            </td>
                                            <td className="vgm-audit-cell vgm-audit-change-cell">
                                                {visibleChanges.length === 0
                                                    ? <span className="vgm-audit-nochange">—</span>
                                                    : (
                                                        <div className="vgm-audit-changes">
                                                            {visibleChanges.map(([fieldName, oldValue]) => {
                                                                const snapshotFieldName = getSnapshotFieldName(fieldName)
                                                                return (
                                                                    <div key={fieldName} className="vgm-audit-change">
                                                                        <span className="vgm-audit-change-field">{humanizeName(fieldName)}</span>
                                                                        <span className="vgm-audit-change-label">From:</span>
                                                                        <ChangeValue value={formatChangeValue(oldValue)} />
                                                                        <span className="vgm-audit-change-label is-to">To:</span>
                                                                        <ChangeValue value={formatChangeValue(item.snapshot?.[snapshotFieldName])} />
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                            </td>
                                            <td className="vgm-audit-cell vgm-audit-snapshot-col">
                                                <button
                                                    type="button"
                                                    className="vgm-audit-toggle"
                                                    aria-expanded={!!isDetailsVisible}
                                                    aria-controls={`${itemKey}-snapshot`}
                                                    onClick={() => toggleDetails(itemKey)}
                                                >
                                                    <i className={`fa fa-chevron-right vgm-audit-toggle-icon${isDetailsVisible ? ' is-open' : ''}`} aria-hidden="true" />
                                                    Snapshot
                                                </button>
                                            </td>
                                        </tr>
                                        {isDetailsVisible && (
                                            <tr className="vgm-audit-snapshot-row">
                                                <td className="vgm-audit-snapshot-body" colSpan={columnCount}>
                                                    <pre id={`${itemKey}-snapshot`} className="vgm-audit-json">{formatJson(item.snapshot)}</pre>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default BQHistoryList
